import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useMount, useUnmount } from "react-use";
import { Alert, AlertTitle, Box, Button, FormLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { ColDef, GridOptions, RowDoubleClickedEvent } from "ag-grid-community";
import { Feature, Polygon } from "geojson";
import rbush from "geojson-rbush";
import { FeatureIdentifier } from "mapbox-gl";

import { LoadingButton } from "ui";

import { selectLayerByVersionAndLayerKey, useGetFeaturesQuery } from "fond/api";
import DrawHandler from "fond/draw/DrawHandler";
import { CTS_STYLE_ID } from "fond/draw/modes/cts/constants";
import { createStyles, getCTSStyles, removeCTSStyles } from "fond/draw/modes/cts/ctsDrawStyles";
import ctsPolygonSelect from "fond/draw/modes/cts/ctsPolygonSelect";
import ctsSelect from "fond/draw/modes/cts/ctsSelect";
import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { MapContext } from "fond/map/MapProvider";
import { zoomTo } from "fond/map/redux";
import { PolygonSelectButton, SelectButton, ToolbarConfig } from "fond/map/Toolbar";
import { EditMode, selectFeature, setEditMode } from "fond/project/redux";
import { booleanPointInPolygon, featureCollection } from "fond/turf";
import { WORKFLOW_KEYS } from "fond/types";
import { filterFeatureCollection } from "fond/utils/filterExpressions";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { AgGrid, useStackedNavigationContext } from "fond/widgets";
import SkeletonLoadingGrid from "fond/widgets/SkeletonLoadingGrid";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";

import PrioritizeButton from "./PrioritizeButton";
import TooManyFeaturesPopup from "./TooManyFeaturesPopup";

interface ExcludeDrawPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
  workflowKey: typeof WORKFLOW_KEYS.NBN_COST_TO_SERVE.ITERATION | typeof WORKFLOW_KEYS.COLLABORATION_CTS.ITERATION;
  layerFilter: any[] | undefined;
  source: string;
  sourceLayer: string;
  identifierLabel?: string;
  identifierProperty?: string;
  /**
   * Flag indicating whether to color the service locations by a Cost to Serve heat map.
   * Used in conjunction with colorByOptions property.
   * @default false
   */
  colorByCTS?: boolean;
  /**
   * Options for coloring service locations if colorByCTS is true.
   */
  colorByOptions?: { label: string; layerId: string }[];
}

const config: ToolbarConfig = [
  {
    id: "mainActions",
    type: "mode",
    items: [<SelectButton key="select" />, <PolygonSelectButton key="polygonSelect" />],
  },
  {
    id: "excludeTools",
    type: "mode",
    items: [<PrioritizeButton key="prioritize" prioritize />, <PrioritizeButton key="defer" prioritize={false} />],
  },
];

const modes = {
  simple_select: ctsSelect,
  polygon_select: ctsPolygonSelect,
};

const ExcludeDrawPanel: React.FC<ExcludeDrawPanelProps> = ({
  isReadOnly,
  workflowKey,
  layerFilter,
  source,
  sourceLayer,
  identifierLabel = "Equipment ID",
  identifierProperty = "equipment_id",
  colorByCTS = false,
  colorByOptions = [],
}) => {
  const dispatch = useAppDispatch();
  const { drawControl, map, setDrawMode } = useContext(MapContext);
  const { goBack } = useStackedNavigationContext();
  const [rowData, setRowData] = useState<Feature[]>([]);
  const versionId = useAppSelector((state) => state.project.versionId);
  const { runWorkflow } = useCustomWorkflow({ versionId, key: workflowKey, refreshVersionDataOnFail: true });
  const { params } = useStackedNavigationContext();
  const scenarioName = params.excludeDraw?.scenarioName || "Untitled Scenario";
  const [colorBy, setColorBy] = useState<string | undefined>(colorByOptions.at(0)?.layerId);
  const [excludedFondIDs, setExcludedFondIDs] = useState<string[]>([]);
  const [isWaitingForWorkflowStart, setIsWaitingForWorkflowStart] = useState<boolean>(false);
  const layer = useAppSelector((state) => selectLayerByVersionAndLayerKey(state, { versionId, layerId: sourceLayer }));
  const { data, isLoading } = useGetFeaturesQuery({ layerId: layer?.ID || "" }, { skip: !layer?.ID });

  // State for showing the "too many features" warning popup
  const [exceededFeatureCount, setExceededFeatureCount] = useState(0);
  const [popupPosition, setPopupPosition] = useState<{ lng: number; lat: number } | null>(null);

  // Create a spatial index and load all features on initial load.
  // This allows for fast spatial queries when selecting features within a polygon
  const tree = useMemo(() => {
    // Deep clone needed because RTK Query freezes objects and rbush adds bbox properties
    const clonedData = structuredClone(data || []);
    return rbush().load(featureCollection(clonedData));
  }, [data]);

  useMount(() => {
    dispatch(setEditMode(EditMode.edit));
  });

  useUnmount(() => {
    dispatch(setEditMode(EditMode.none));
    if (colorByCTS && map) removeCTSStyles(map);
  });

  useEffect(() => {
    if (colorByCTS && colorBy && map) {
      // Remove any existing CTS styles
      removeCTSStyles(map);

      // Add the CTS styles for the selected colorBy
      getCTSStyles(map, colorBy, excludedFondIDs).forEach((style) => {
        map.addLayer(style, CTS_STYLE_ID);
      });
    }
    // Note: isLoading is required in deps to ensure styles are applied after vector tile source is ready
  }, [colorBy, colorByCTS, map, excludedFondIDs, isLoading]);

  const drawingStyles = useMemo(
    () => createStyles({ styleId: CTS_STYLE_ID, source, sourceLayer, layerFilter, withHeatmap: colorByCTS }),
    [layerFilter, source, sourceLayer, colorByCTS]
  );

  const getFeaturesInBoundary = useCallback(
    (polygon: Feature<Polygon>) => {
      if (!data) return [];
      // Search for all features that intersect with the polygon's bounding box
      const results = tree.search(polygon);

      // If a layerFilter is provided, filter the results to only include features that match the specified layer filter
      let filteredResults;
      try {
        filteredResults = layerFilter ? filterFeatureCollection(results, layerFilter).features : results.features;
      } catch (error) {
        // Fall back to unfiltered results if filter is invalid
        filteredResults = results.features;
      }

      const featuresInsidePolygon = filteredResults.filter((feature) => {
        // Assuming point features - adjust if needed
        return booleanPointInPolygon(feature, polygon);
      });

      return featuresInsidePolygon;
    },
    [data, layerFilter, tree]
  );

  const onSelectionLimitExceeded = useCallback(
    (featureCount: number) => {
      setExceededFeatureCount(featureCount);
      // Capture the map center position when showing the popup
      if (map) {
        const center = map.getCenter();
        setPopupPosition({ lng: center.lng, lat: center.lat });
      }
    },
    [map]
  );

  const handleCloseLimitPopup = useCallback(() => {
    setPopupPosition(null);
  }, []);

  const sources: FeatureIdentifier[] = useMemo(() => [{ source, sourceLayer }], [source, sourceLayer]);
  const drawHandlerOptions = useMemo(
    () => ({
      selectableLayerIds: [CTS_STYLE_ID],
      selectableLayerFilter: layerFilter,
      selectableSource: source,
      selectableSourceLayer: sourceLayer,
      // Add options for fetching precise geometry
      getFeaturesInBoundary,
      onSelectionLimitExceeded,
      versionId,
      layerID: layer?.ID,
    }),
    [layerFilter, source, sourceLayer, getFeaturesInBoundary, onSelectionLimitExceeded, versionId, layer?.ID]
  );

  const columns: ColDef[] = useMemo(
    () =>
      [
        {
          headerName: identifierLabel,
          field: `properties.${identifierProperty}`,
          flex: 1,
        },
      ] as ColDef[],
    [identifierLabel, identifierProperty]
  );

  const gridOptions: GridOptions = useMemo(
    () => ({
      animateRows: true,
      rowGroupPanelShow: "never",
      sideBar: false,
      pagination: false,
      suppressMovableColumns: true,
      domLayout: "normal",
    }),
    []
  );

  const handleOnSelectionChange = useCallback(
    (event: MapboxDraw.DrawSelectionChangeEvent) => {
      if (event.features.length === 1) {
        dispatch(selectFeature(event.features[0]));
      }
    },
    [dispatch]
  );

  const handleColorByChange = (event: SelectChangeEvent<string>) => {
    setColorBy(event.target.value);
  };

  const handleOnRender = useCallback(() => {
    const excludedFeatures = drawControl.current?.getAll().features.filter(({ properties }) => properties?.excluded === "true") || [];
    setRowData(excludedFeatures);
    setExcludedFondIDs(excludedFeatures.map(({ properties }) => properties?.FondID).filter(Boolean));
  }, [drawControl]);

  useEffect(() => {
    map?.on("draw.selectionchange", handleOnSelectionChange);
    map?.on("draw.prioritize", handleOnRender);

    return () => {
      map?.off("draw.selectionchange", handleOnSelectionChange);
      map?.off("draw.prioritize", handleOnRender);
    };
  }, [dispatch, handleOnRender, handleOnSelectionChange, map]);

  const handleOnConfirm = async () => {
    // Set flag to wait for workflow to start before navigating back
    setIsWaitingForWorkflowStart(true);

    const payload = rowData.filter(({ id }) => typeof id === "string").map(({ id }) => ({ ID: id }));
    await runWorkflow?.({
      FeatureSelection: {
        Mode: "exclude",
        Features: {
          Type: "id",
          Ids: payload,
        },
      },
      TargetVersionName: scenarioName,
    });

    handleBack();
  };

  /**
   * Callback function called when a row is double clicked
   * Feature is zoomed to & selected
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    drawControl.current?.changeMode("simple_select", { featureIds: [event.data.properties.id] });
    dispatch(zoomTo([event.data]));
    dispatch(selectFeature(event.data));
  };

  const handleBack = () => {
    // For each of the features selected during the draw, restore the feature state isEditing = false
    drawControl.current?.getAll().features.forEach(({ id }) => {
      map?.setFeatureState({ id, source, sourceLayer }, { isEditing: false });
    });

    setDrawMode("no_feature");
    goBack();
  };

  if (isLoading) {
    return <SkeletonLoadingGrid spacing={2} items={[{ height: 40 }, { height: 130 }, { height: 38 }, { height: 300 }]} />;
  }

  return (
    <>
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Exclude service locations" />
      <Alert severity="info">
        <AlertTitle>Exclude service locations</AlertTitle>
        Choose the service locations you'd like to exclude from this scenario. When you're finished, click "Confirm" to proceed.
      </Alert>

      {colorByCTS && (
        <Box sx={{ mt: 2 }} display="flex" alignItems="center">
          <FormLabel sx={{ textWrap: "nowrap" }}>Color by:</FormLabel>
          <Select value={colorBy} onChange={handleColorByChange} fullWidth size="small" sx={{ ml: 1 }}>
            {colorByOptions.map(({ label, layerId }) => (
              <MenuItem key={layerId} value={layerId}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}

      <Box sx={{ height: 300, width: "100%", mt: 2 }}>
        <AgGrid
          rowData={rowData}
          columnDefs={columns}
          gridOptions={gridOptions}
          variant="outlined"
          size="compact"
          onRowDoubleClicked={onRowDoubleClicked}
        />
      </Box>

      <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
        <Button color="primary" size="small" onClick={handleBack} disabled={isWaitingForWorkflowStart}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          size="small"
          onClick={handleOnConfirm}
          sx={{ ml: 1, px: 2 }}
          data-testid="finish-button"
          disabled={rowData.length === 0 || isReadOnly}
          loading={isWaitingForWorkflowStart}
        >
          Confirm and run new scenario
        </LoadingButton>
      </Box>

      <DrawHandler
        startingMode="simple_select"
        initialFeatures={null}
        sources={sources}
        config={config}
        options={drawHandlerOptions}
        styles={drawingStyles}
        modes={modes}
      />

      <TooManyFeaturesPopup map={map} popupPosition={popupPosition} exceededFeatureCount={exceededFeatureCount} onClose={handleCloseLimitPopup} />
    </>
  );
};

export default ExcludeDrawPanel;
